// src/crm/CreatorDetailPage.tsx
import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAppStore } from '../shell/appStore'
import { dealRoi } from '../lib/roi'
import { DealForm } from './components/DealForm'
import { useCreateDeal } from './hooks/useDeals'
import { STAGE_LABELS } from './pipeline/stages'
import type { Creator, Deal, Campaign, DealStage, App } from '../lib/types'
import { ConversationThread } from './conversations/ConversationThread'
import { referenceDeal } from './templates/resolve'
import { MemberPanel } from './ugc/MemberPanel'

const COLLAB_LABELS: Record<string, string> = {
  audience: 'Audience',
  production: 'Production',
  both: 'Audience + Production',
}

const STAGE_COLORS: Record<DealStage, string> = {
  prospecte: 'var(--dim)',
  contacte: 'var(--muted)',
  en_discussion: 'var(--bronze)',
  accepte: '#2A678D',
  brief_envoye: '#2A678D',
  contenu_recu: 'var(--arancio)',
  publie: '#4a9a5c',
  paye_cloture: '#4a9a5c',
  decline_archive: 'var(--dim)',
}

function formatNumber(n: number | null): string {
  if (n === null) return '—'
  return new Intl.NumberFormat('fr-FR').format(n)
}

function SocialRow({ social }: { social: Creator['socials'][number] }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.5rem 0',
        borderBottom: '1px solid var(--line)',
      }}
    >
      <span style={{ fontWeight: 600, color: 'var(--fg)', minWidth: '80px', fontSize: '0.875rem' }}>
        {social.platform}
      </span>
      {social.profileUrl ? (
        <a
          href={social.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--bronze)', fontSize: '0.875rem' }}
        >
          {social.handle ?? social.profileUrl}
        </a>
      ) : (
        <span style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>{social.handle}</span>
      )}
      {social.followers !== null && (
        <span style={{ marginLeft: 'auto', color: 'var(--muted)', fontSize: '0.8125rem' }}>
          {formatNumber(social.followers)} abonnés
        </span>
      )}
      {social.engagementRate !== null && (
        <span style={{ color: 'var(--dim)', fontSize: '0.8125rem' }}>
          {social.engagementRate.toFixed(1)}% eng.
        </span>
      )}
    </div>
  )
}

function DealRow({ deal }: { deal: Deal }) {
  const roi = dealRoi(deal)
  return (
    <div
      style={{
        background: 'var(--elevated)',
        border: '1px solid var(--line)',
        borderRadius: '8px',
        padding: '0.875rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <span
          data-testid="deal-stage-badge"
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: STAGE_COLORS[deal.stage],
            background: 'var(--raised)',
            border: '1px solid var(--line)',
            borderRadius: '4px',
            padding: '0.125rem 0.5rem',
          }}
        >
          {STAGE_LABELS[deal.stage]}
        </span>
        {deal.compModel && (
          <span style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>
            {deal.compModel === 'flat'
              ? `${formatNumber(deal.amount)} ${deal.currency}`
              : deal.compModel}
          </span>
        )}
        {deal.promoCode && (
          <span
            style={{
              fontSize: '0.75rem',
              fontFamily: 'var(--mono)',
              color: 'var(--bronze)',
              background: 'var(--raised)',
              border: '1px solid var(--line)',
              borderRadius: '4px',
              padding: '0.125rem 0.375rem',
            }}
          >
            {deal.promoCode}
          </span>
        )}
        {deal.dueDate && (
          <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: 'var(--dim)' }}>
            Échéance : {deal.dueDate}
          </span>
        )}
      </div>
      {deal.deliverables.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '1.5rem',
            fontSize: '0.8125rem',
            color: 'var(--muted)',
          }}
        >
          <span>{formatNumber(roi.views)} impressions</span>
          <span>{formatNumber(roi.installs)} téléchargements</span>
          {roi.cpi !== null && <span>CPI {roi.cpi.toFixed(2)} €</span>}
        </div>
      )}
    </div>
  )
}

export function CreatorDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { activeAppId } = useAppStore()
  const queryClient = useQueryClient()
  const [showDealForm, setShowDealForm] = useState(false)

  const { data: creator, isLoading: creatorLoading } = useQuery<Creator>({
    queryKey: ['creator', id],
    queryFn: () =>
      api.get<{ creator: Creator }>(`/api/creators/${id}`).then((r) => r.creator),
    enabled: id !== undefined,
  })

  const { data: deals = [] } = useQuery<Deal[]>({
    queryKey: ['deals', activeAppId],
    queryFn: () =>
      api
        .get<{ deals: Deal[] }>(`/api/deals?appId=${activeAppId}`)
        .then((r) => r.deals),
    enabled: activeAppId !== null,
  })

  const { data: campaigns = [] } = useQuery<Campaign[]>({
    queryKey: ['campaigns', activeAppId],
    queryFn: () =>
      api
        .get<{ campaigns: Campaign[] }>(`/api/campaigns?appId=${activeAppId}`)
        .then((r) => r.campaigns),
    enabled: activeAppId !== null,
  })

  // ⚠️ Recopie à l'identique de la requête d'AppSwitcher (src/shell/AppSwitcher.tsx) :
  // même queryKey ['apps'] ⇒ même entrée de cache. Toute divergence de forme
  // (déballer l'enveloppe, changer staleTime) casserait l'un des deux consommateurs.
  const { data: appsData } = useQuery<{ apps: App[] }, Error>({
    queryKey: ['apps'],
    queryFn: () => api.get<{ apps: App[] }>('/api/apps'),
    staleTime: 1000 * 60 * 5,
  })
  const appName = (appsData?.apps ?? []).find((a) => a.id === activeAppId)?.name ?? ''

  const createDeal = useCreateDeal(queryClient)

  const creatorDeals = deals.filter((d) => d.creatorId === id)

  // Agrégation ROI globale sur tous les deals
  const globalRoi = creatorDeals.reduce(
    (acc, deal) => {
      const r = dealRoi(deal)
      return {
        installs: acc.installs + r.installs,
        views: acc.views + r.views,
        revenue: acc.revenue + r.revenue,
      }
    },
    { installs: 0, views: 0, revenue: 0 },
  )

  if (creatorLoading || !creator) {
    return (
      <div style={{ padding: '2rem 2.5rem', color: 'var(--muted)' }}>Chargement…</div>
    )
  }

  return (
    <div style={{ padding: '2rem 2.5rem', maxWidth: '860px' }}>
      {/* En-tête */}
      <div style={{ marginBottom: '0.5rem' }}>
        <Link
          to="/crm/contacts"
          style={{ fontSize: '0.8125rem', color: 'var(--muted)', textDecoration: 'none' }}
        >
          ← Contacts
        </Link>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <div style={{ flex: 1 }}>
          <h1
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--fg)',
              letterSpacing: '-0.025em',
              margin: 0,
            }}
          >
            {creator.displayName}
          </h1>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '0.375rem',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--arancio)',
                background: 'rgba(217,83,30,0.12)',
                border: '1px solid rgba(217,83,30,0.3)',
                borderRadius: '4px',
                padding: '0.125rem 0.5rem',
              }}
            >
              {COLLAB_LABELS[creator.collaborationType] ?? creator.collaborationType}
            </span>
            {creator.niche && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--muted)' }}>{creator.niche}</span>
            )}
            {creator.audienceCountry && (
              <span style={{ fontSize: '0.8125rem', color: 'var(--dim)' }}>
                {creator.audienceCountry}
                {creator.audienceLanguage ? ` · ${creator.audienceLanguage}` : ''}
              </span>
            )}
            {creator.email && (
              <a
                href={`mailto:${creator.email}`}
                style={{ fontSize: '0.8125rem', color: 'var(--bronze)' }}
              >
                {creator.email}
              </a>
            )}
            {creator.source && (
              <span
                data-testid="creator-source"
                style={{ fontSize: '0.75rem', color: 'var(--dim)' }}
              >
                {creator.source}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Socials */}
      {creator.socials.length > 0 && (
        <section style={{ marginBottom: '1.5rem' }}>
          <h2
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: '0.5rem',
            }}
          >
            Plateformes
          </h2>
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: '8px',
              padding: '0 0.875rem',
            }}
          >
            {creator.socials.map((s) => (
              <SocialRow key={s.id} social={s} />
            ))}
          </div>
        </section>
      )}

      {/* Résumé ROI */}
      {creatorDeals.length > 0 && (
        <section style={{ marginBottom: '1.5rem' }}>
          <h2
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: '0.5rem',
            }}
          >
            ROI cumulé
          </h2>
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
            }}
          >
            {(
              [
                { label: 'Impressions', value: formatNumber(globalRoi.views) },
                { label: 'Installations', value: formatNumber(globalRoi.installs) },
                { label: 'Revenus', value: `${formatNumber(globalRoi.revenue)} €` },
              ] as { label: string; value: string }[]
            ).map(({ label, value }) => (
              <div
                key={label}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: '8px',
                  padding: '0.875rem 1.25rem',
                  minWidth: '140px',
                }}
              >
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                  {label}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--fg)' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Deals */}
      <section>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
          }}
        >
          <h2
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              margin: 0,
            }}
          >
            Deals ({creatorDeals.length})
          </h2>
          <button
            onClick={() => setShowDealForm(true)}
            style={{
              padding: '0.375rem 0.875rem',
              background: 'var(--arancio)',
              border: 'none',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Nouveau deal
          </button>
        </div>

        {showDealForm && (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: '8px',
              padding: '1.25rem',
              marginBottom: '1rem',
            }}
          >
            <h3
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--fg)',
                margin: '0 0 1rem',
              }}
            >
              Nouveau deal
            </h3>
            <DealForm
              creatorId={creator.id}
              appId={activeAppId ?? ''}
              campaigns={campaigns}
              onSave={(payload) => {
                createDeal.mutate(payload, {
                  onSuccess: () => setShowDealForm(false),
                })
              }}
              onCancel={() => setShowDealForm(false)}
            />
          </div>
        )}

        {creatorDeals.length === 0 && !showDealForm ? (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: '8px',
              padding: '2rem',
              textAlign: 'center',
              color: 'var(--dim)',
              fontSize: '0.875rem',
            }}
          >
            Aucun deal pour cette app. Créez le premier.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {creatorDeals.map((deal) => (
              <DealRow key={deal.id} deal={deal} />
            ))}
          </div>
        )}
      </section>

      {/* Programme UGC (spec § 6.5) */}
      {creator.contactType === 'ugc_creator' && (
        <div style={{ marginTop: '1.5rem' }}>
          <MemberPanel creator={creator} />
        </div>
      )}

      {/* Fil de conversation */}
      <section style={{ marginTop: '1.5rem' }}>
        <h2
          style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: '0.75rem',
          }}
        >
          Échanges
        </h2>
        <ConversationThread
          creatorId={creator.id}
          creatorEmail={creator.email}
          contact={creator}
          deal={referenceDeal(creatorDeals, creator.id)}
          appName={appName}
        />
      </section>

      {/* Notes */}
      {creator.notes && (
        <section style={{ marginTop: '1.5rem' }}>
          <h2
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: '0.5rem',
            }}
          >
            Notes
          </h2>
          <p
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--line)',
              borderRadius: '8px',
              padding: '0.875rem 1rem',
              fontSize: '0.875rem',
              color: 'var(--fg)',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {creator.notes}
          </p>
        </section>
      )}
    </div>
  )
}
